// Renders the markdown subset the agent actually emits — paragraphs, **bold**,
// `inline code`, ordered/unordered lists, preserved line breaks — as React nodes.
// Never HTML: agent output is untrusted, so there is no innerHTML path here.

import { Box, Typography } from '@mui/material';
import React from 'react';

const INLINE = /`([^`\n]+)`|\*\*([^\n]+?)\*\*/g;
const LIST_ITEM = /^ {0,3}(?:([-*+])|(\d{1,9})[.)])\s+(.*)$/;
// ATX headings, with the optional closing run of #'s dropped. Rendered as bold body
// text rather than a real <h*> — these are section labels inside a chat bubble.
const HEADING = /^ {0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;

const codeSx = {
  px: 0.5,
  borderRadius: 0.5,
  fontFamily: 'monospace',
  fontSize: '0.85em',
  bgcolor: 'action.hover',
} as const;

const listSx = { m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 } as const;

const inlineNodes = (text: string, keyPrefix: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  let last = 0;
  INLINE.lastIndex = 0;
  let match = INLINE.exec(text);
  while (match) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${out.length}`;
    if (match[1] !== undefined) {
      out.push(
        <Box component="code" key={key} sx={codeSx}>
          {match[1]}
        </Box>
      );
    } else {
      out.push(<strong key={key}>{match[2]}</strong>);
    }
    last = match.index + match[0].length;
    match = INLINE.exec(text);
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

type Block =
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'heading'; text: string }
  | { kind: 'list'; ordered: boolean; start: number; items: string[] };

const toBlocks = (text: string): Block[] => {
  const blocks: Block[] = [];
  let current: Block | null = null;

  text.split('\n').forEach((line) => {
    if (!line.trim()) {
      current = null;
      return;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      current = null;
      blocks.push({ kind: 'heading', text: heading[2] });
      return;
    }
    const item = LIST_ITEM.exec(line);
    if (item) {
      const ordered = item[2] !== undefined;
      if (current?.kind === 'list' && current.ordered === ordered) {
        current.items.push(item[3]);
        return;
      }
      current = { kind: 'list', ordered, start: ordered ? Number(item[2]) : 1, items: [item[3]] };
      blocks.push(current);
      return;
    }
    if (current?.kind === 'paragraph') {
      current.lines.push(line);
      return;
    }
    current = { kind: 'paragraph', lines: [line] };
    blocks.push(current);
  });

  return blocks;
};

export const renderMarkdown = (text: string): React.ReactNode => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
    {toBlocks(text).map((block, index) => {
      const key = `b${index}`;
      if (block.kind === 'heading') {
        return (
          <Typography
            key={key}
            variant="body2"
            fontWeight={700}
            sx={{ mt: index === 0 ? 0 : 0.5, overflowWrap: 'anywhere' }}
          >
            {inlineNodes(block.text, key)}
          </Typography>
        );
      }
      if (block.kind === 'paragraph') {
        return (
          <Typography key={key} variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {inlineNodes(block.lines.join('\n'), key)}
          </Typography>
        );
      }
      const items = block.items.map((item, i) => (
        // Blocks are derived from immutable text, so the index is stable.
        // eslint-disable-next-line react/no-array-index-key
        <Typography key={`${key}-${i}`} component="li" variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          {inlineNodes(item, `${key}-${i}`)}
        </Typography>
      ));
      return block.ordered ? (
        <Box key={key} component="ol" start={block.start === 1 ? undefined : block.start} sx={listSx}>
          {items}
        </Box>
      ) : (
        <Box key={key} component="ul" sx={listSx}>
          {items}
        </Box>
      );
    })}
  </Box>
);

export default renderMarkdown;
